// See https://dev.bandwidth.com/docs/messaging/webhooks/ for detailed info on each field

import { dateOrStringAsDate } from "@redotech/zod-util/db-coercion";
import * as z from "zod";

export enum BandwidthWebhookType {
  // Inbound (texts we receive)
  INBOUND_MESSAGE_RECEIVED = "message-received",
  // Outbound (texts we sent)
  OUTBOUND_MESSAGE_SENDING = "message-sending",
  OUTBOUND_MESSAGE_DELIVERED = "message-delivered",
  OUTBOUND_MESSAGE_FAILED = "message-failed",
  OUTBOUND_MESSAGE_READ = "message-read",
}

const bandwidthMessageReceivedWebhookSchema = z.object({
  type: z.literal(BandwidthWebhookType.INBOUND_MESSAGE_RECEIVED),
  time: dateOrStringAsDate,
  to: z.string(),
  description: z.string().nullish(),
  message: z.object({
    id: z.string(),
    owner: z.string(),
    applicationId: z.string(),
    time: dateOrStringAsDate,
    segmentCount: z.number().nullish(),
    direction: z.string(),
    to: z.array(z.string()),
    from: z.string(),
    text: z.string().nullish(),
    tag: z.string().nullish(),
    media: z.array(z.string()).nullish(),
  }),
});
export type BandwidthMessageReceivedWebhook = z.infer<
  typeof bandwidthMessageReceivedWebhookSchema
>;

const baseBandwidthMessageWebhookSchema = z.object({
  time: dateOrStringAsDate,
  description: z.string().nullish(),
  to: z.string(),
  message: z.object({
    id: z.string(),
    owner: z.string(),
    time: dateOrStringAsDate,
    direction: z.string(),
    to: z.array(z.string()),
    from: z.string(),
    text: z.string().nullish(), // will be empty, according to docs
    applicationId: z.string(),
    media: z.array(z.string()).nullish(),
    tag: z.string().nullish(),
    segmentCount: z.number().nullish(),
  }),
});
export type BaseBandwidthMessageWebhook = z.infer<
  typeof baseBandwidthMessageWebhookSchema
>;

const bandwidthMessageSentWebhookSchema =
  baseBandwidthMessageWebhookSchema.extend({
    type: z.literal(BandwidthWebhookType.OUTBOUND_MESSAGE_SENDING),
  });
export type BandwidthMessageSentWebhook = z.infer<
  typeof bandwidthMessageSentWebhookSchema
>;

const bandwidthMessageDeliveredWebhookSchema =
  baseBandwidthMessageWebhookSchema.extend({
    type: z.literal(BandwidthWebhookType.OUTBOUND_MESSAGE_DELIVERED),
  });
export type BandwidthMessageDeliveredWebhook = z.infer<
  typeof bandwidthMessageDeliveredWebhookSchema
>;

const bandwidthMessageFailedWebhookSchema =
  baseBandwidthMessageWebhookSchema.extend({
    type: z.literal(BandwidthWebhookType.OUTBOUND_MESSAGE_FAILED),
    errorCode: z.number().nullish(),
    // description field becomes the error message
  });
export type BandwidthMessageFailedWebhook = z.infer<
  typeof bandwidthMessageFailedWebhookSchema
>;

/** Only applies to RCS */
const bandwidthMessageReadWebhookSchema =
  baseBandwidthMessageWebhookSchema.extend({
    type: z.literal(BandwidthWebhookType.OUTBOUND_MESSAGE_READ),
    // why eventTime instead of time is beyond me - both fields exist for this event type
    eventTime: dateOrStringAsDate,
  });
export type BandwidthMessageReadWebhook = z.infer<
  typeof bandwidthMessageReadWebhookSchema
>;

export const bandwidthWebhookSchema = z.discriminatedUnion("type", [
  bandwidthMessageReceivedWebhookSchema,
  bandwidthMessageSentWebhookSchema,
  bandwidthMessageDeliveredWebhookSchema,
  bandwidthMessageFailedWebhookSchema,
  bandwidthMessageReadWebhookSchema,
]);
export type BandwidthWebhook = z.infer<typeof bandwidthWebhookSchema>;

// Bandwidth sends webhooks as an array, even when there's only one event
// See https://dev.bandwidth.com/docs/messaging/webhooks/
export const bandwidthWebhookArraySchema = z.array(bandwidthWebhookSchema);
export type BandwidthWebhookArray = z.infer<typeof bandwidthWebhookArraySchema>;

// Port-in webhook schemas (XML-based, from Numbers API)
// See https://dev.bandwidth.com/docs/numbers/webhooks/orderWebhook/
export enum BandwidthPortInStatus {
  PENDING_DOCUMENTS = "PENDING_DOCUMENTS",
  SUBMITTED = "SUBMITTED",
  FOC = "FOC",
  REQUESTED_SUPP = "REQUESTED_SUPP",
  COMPLETE = "COMPLETE",
  CANCELLED = "CANCELLED",
  EXCEPTION = "EXCEPTION",
  REQUESTED_CANCEL = "REQUESTED_CANCEL",
}

export const bandwidthPortInWebhookSchema = z.object({
  Notification: z.object({
    SubscriptionId: z.string().nullish(),
    OrderType: z.string(),
    OrderId: z.string(),
    CustomerOrderId: z.string().nullish(),
    Status: z.enum(BandwidthPortInStatus),
    Message: z.string().nullish(),
    Note: z.string().nullish(),
    CompletedTelephoneNumbers: z
      .object({ TelephoneNumber: z.union([z.string(), z.array(z.string())]) })
      .nullish(),
    LastModifiedDate: z.string().nullish(),
    ActualFocDate: z.string().nullish(),
  }),
});

export type BandwidthPortInWebhook = z.infer<
  typeof bandwidthPortInWebhookSchema
>;

// ============================================================================
// Toll-Free Verification Schemas
// ============================================================================

export const bandwidthTollFreeVerificationRequestSchema = z.object({
  businessAddress: z.object({
    name: z.string(),
    addr1: z.string(),
    addr2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    url: z.string(),
  }),
  businessContact: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNumber: z.string(),
  }),
  useCase: z.string(),
  useCaseSummary: z.string(),
  messageVolume: z.number(),
  productionMessageContent: z.string(),
  optInWorkflow: z.object({
    description: z.string(),
    imageUrls: z.array(z.string()),
  }),
  phoneNumbers: z.array(z.string()),
  businessRegistrationNumber: z.string(),
  businessRegistrationType: z.string(),
  businessEntityType: z.string(),
  businessRegistrationIssuingCountry: z.string(),
});

export type BandwidthTollFreeVerificationRequest = z.infer<
  typeof bandwidthTollFreeVerificationRequestSchema
>;

// ============================================================================
// Numbers API Response Schemas (XML-based)
// ============================================================================

// The XML parser flattens the structure to this exact format
export const bandwidthSearchAvailableNumbersResponseSchema = z.object({
  phoneNumbers: z.array(z.string()).optional(),
  TelephoneNumberList: z.array(z.string()).optional(),
  ResultCount: z.number().optional(),
  resultCount: z.number().optional(),
});

export type BandwidthSearchAvailableNumbersResponse = z.infer<
  typeof bandwidthSearchAvailableNumbersResponseSchema
>;

export const bandwidthOrderNumberResponseSchema = z.object({
  OrderResponse: z
    .object({
      Order: z.object({ id: z.string().optional() }).optional(),
      OrderStatus: z.string().optional(),
    })
    .optional(),
  // Direct properties (when OrderResponse is the root)
  Order: z.object({ id: z.string().optional() }).optional(),
  OrderStatus: z.string().optional(),
});

export type BandwidthOrderNumberResponse = z.infer<
  typeof bandwidthOrderNumberResponseSchema
>;

export const bandwidthSearchNumberParamsSchema = z.object({
  areaCode: z.string().optional(),
  tollFreeWildCard: z.string().optional(),
  quantity: z.number().optional(),
  enableTNDetail: z.boolean().optional(),
});

export type BandwidthSearchNumberParams = z.infer<
  typeof bandwidthSearchNumberParamsSchema
>;

// ============================================================================
// Order Webhook Schemas
// ============================================================================

export enum BandwidthOrderStatus {
  RECEIVED = "RECEIVED",
  PROCESSING = "PROCESSING",
  COMPLETE = "COMPLETE",
  PARTIAL = "PARTIAL",
  BACKORDERED = "BACKORDERED",
  FAILED = "FAILED",
  NEW = "NEW",
}

// v2 subscription payload (JSON) — "Bandwidth App Order Update" event type
export const bandwidthOrderWebhookSchema = z.object({
  orderId: z.string(),
  orderType: z.string(),
  status: z.enum(BandwidthOrderStatus).optional(), // absent on "note" sub-events
  message: z.string().optional(),
  customerOrderId: z.string().optional(),
  completedPhoneNumbers: z.array(z.string()).optional(),
  phoneNumbers: z.array(z.string()).optional(),
  lastModifiedDate: z.string().optional(),
});

export type BandwidthOrderWebhook = z.infer<typeof bandwidthOrderWebhookSchema>;

// ============================================================================
// Toll-Free Verification Webhook Schemas
// ============================================================================

export enum BandwidthTollFreeVerificationStatus {
  Verified = "VERIFIED",
  Unverified = "UNVERIFIED",
}

export const bandwidthTollFreeVerificationWebhookSchema = z.object({
  phoneNumber: z.string().regex(/^\+1(800|833|844|855|866|877|888)[2-9]\d{6}$/),
  status: z.enum(BandwidthTollFreeVerificationStatus),
});

export type BandwidthTollFreeVerificationWebhook = z.infer<
  typeof bandwidthTollFreeVerificationWebhookSchema
>;

// Verification status response (from GET API, not webhook)
// See: https://dev.bandwidth.com/apis/messaging/#tag/Campaigns/operation/GetTnOptionOrder
export const bandwidthVerificationStatusResponseSchema = z.object({
  phoneNumber: z.string(),
  status: z.enum(["VERIFIED", "UNVERIFIED", "PENDING"]),
  declineReasonDescription: z.string().optional(),
  blocked: z.boolean().optional(),
  blockedReason: z.string().optional(),
});

export type BandwidthVerificationStatusResponse = z.infer<
  typeof bandwidthVerificationStatusResponseSchema
>;
